import { memo, useContext } from 'react';
import { ThemeProvider } from '@emotion/react';
import { useRouter } from 'next/router';
import styled, { type CSSObject } from '@emotion/styled';

import type { Theme, ThemeProps } from '@emotion/react';

import { isClient } from '../../lib/utils';
import bp from '../../lib/cssMediaQuery';
import useThemeStyles from '../../lib/useThemeStyles';
import { useLiteSiteNav } from '../../lib/useNavLinks';
import GoogleFonts from '../Layout/GoogleFonts';
import LiteNav from '../Layout/Nav/Lite';
import LiteFooter from '../Layout/Footer/Lite';
import LiteSitePage from '../LiteSitePage';
import PageContents from '../PageContents';
import PageContentsSkeleton from '../PageContentsSkeleton';
import { WWContext, type WWContextValue } from '../WWContext';
import RegistryTemplate from './RegistryTemplate';
import {
  isWwsWeddingWebsiteV2,
  type Wws_Page,
  type Wws_PageItem,
} from '../../types/graphql';

import type { TemplateProps } from './BaseTemplate';
import MobileHomePageNav from '../MobileHomePageNav';
import DateLocationLockup from '../DateLocationLockup';
import { useWWSQueryContext } from '../../context/WWSQueryContext';

const Background = styled('div')`
  background: #c4c4c4;
  color: #646464;
  height: 100vh;
  min-height: 40vw;
  position: relative;

  ${bp['max768']} {
    height: auto;
    min-height: 100vh;

    &.showNav {
      padding-top: 60px;
    }
  }

  ${({ theme }: ThemeProps): CSSObject | undefined =>
    theme.liteSite?.CardLayout?.components?.Background}
`;

const Content = styled('div')`
  flex-wrap: wrap;
  margin: 0px auto;
  min-height: calc(100vh - 8rem);
  align-items: stretch;

  ${bp['max768']} {
    min-height: auto;
    display: block;
  }

  &.nonav {
    ${bp['min768']} {
      min-height: 100vh;
    }

    ${({ theme }: ThemeProps): CSSObject | undefined =>
      theme.liteSite?.CardLayout?.components?.NoNavContent}
  }

  ${({ theme }: ThemeProps): CSSObject | undefined =>
    theme.liteSite?.CardLayout?.components?.Content}
`;

const SecondaryPage = styled('div')`
  flex-wrap: wrap;
  margin: 0px auto;
  min-height: calc(100vh - 8rem);
  align-items: stretch;

  @media (max-width: 768px) {
    min-height: auto;
    display: block;
  }

  ${({ theme }: ThemeProps): CSSObject | undefined =>
    theme.liteSite?.CardLayout?.components?.Content}

  ${({ theme }: ThemeProps): CSSObject | undefined =>
    theme.liteSite?.CardLayout?.components?.Secondary}
`;

const SecondaryContent = styled('div')`
  margin: 0 auto;

  ${({ theme }: ThemeProps): CSSObject | undefined =>
    theme.liteSite?.CardLayout?.components?.SecondaryContent}
`;

const contentStyle = { display: 'flex' };

function LiteSiteTemplate({ themeOverride }: TemplateProps) {
  const {
    replace,
    query: {
      route,
      pathPrefix,
      slug,
      renderingPreviewImage = 'false',
      preview = 'true',
    },
  } = useRouter();

  const { data } = useContext<WWContextValue>(WWContext);
  const { isFullSiteDataLoaded, initialRouteName } = useWWSQueryContext();

  const { showNav } = useLiteSiteNav();

  const theme: Partial<Theme> = useThemeStyles(themeOverride);

  const routeName = Array.isArray(route) ? route[0] : !route ? '' : route;

  if (isWwsWeddingWebsiteV2(data?.weddingWebsiteResult)) {
    const currentPage = data?.weddingWebsiteResult.pages?.find(
      (page: Wws_Page): boolean => page.routeName === routeName
    );

    // If any page besides page with routeName = / is hidden redirect
    if (
      !currentPage ||
      (currentPage &&
        currentPage.routeName !== '' &&
        !preview &&
        !currentPage.show)
    ) {
      // Next/Router can only be used client side, do the routing client side
      if (isClient()) {
        replace(`/${pathPrefix}/${slug}`);

        return null;
      }

      return null;
    }

    const coverPhotoItem = data?.weddingWebsiteResult.pages
      ?.find((page: Wws_Page): boolean => page.type === 'LiteSitePage')
      ?.items.find(
        (item: Wws_PageItem): boolean => item.type === 'CoverPhotoItem'
      );

    // TODO: Match Page item to proper layout items
    return (
      <ThemeProvider theme={theme}>
        <GoogleFonts />

        <Background
          className={`${showNav ? 'showNav ' : ''}${
            !coverPhotoItem ? 'noPhoto ' : ''
          }${currentPage.type === 'LiteSitePage' ? 'home' : 'secondary'}`}
        >
          <LiteNav />

          {currentPage.type === 'LiteSitePage' ? (
            <Content
              style={contentStyle}
              className={`${!showNav ? 'nonav' : ''}`}
            >
              <LiteSitePage
                renderingPreviewImage={renderingPreviewImage === 'true'}
                weddingWebsiteResult={data?.weddingWebsiteResult}
              />
            </Content>
          ) : null}

          {currentPage.type === 'RegistryPage' ? (
            <SecondaryPage>
              <RegistryTemplate />;
            </SecondaryPage>
          ) : null}

          {currentPage.type !== 'LiteSitePage' &&
          currentPage.type !== 'RegistryPage' ? (
            <SecondaryPage>
              <SecondaryContent>
                {currentPage.type === 'WeddingPage' && <DateLocationLockup />}
                {(routeName || '') === initialRouteName ||
                isFullSiteDataLoaded ? (
                  <PageContents
                    page={currentPage}
                    photoAlbums={data?.weddingWebsiteResult?.photoAlbums}
                  />
                ) : (
                  <PageContentsSkeleton />
                )}
                {currentPage.type === 'WeddingPage' ? (
                  <MobileHomePageNav />
                ) : null}
              </SecondaryContent>
            </SecondaryPage>
          ) : null}

          <LiteFooter />
        </Background>
      </ThemeProvider>
    );
  }

  return null;
}

export default memo(LiteSiteTemplate);
